import React from "react";
import { ArrowRight, Copy, Share, Zap } from "lucide-react";
import { CircularProgress } from "./CircularProgress";
import { HowItWorks } from "./HowItWorks";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { LeaderboardEventWithUserStatus } from "@crypto-trading/common";

interface NewReferralScreenProps {
  referralCode: string;
  referralCount: number;
  userRank: number | null;
  codeNeedsRefresh: boolean;
  codeUsedCount: number;
  codeMaxUses: number;
  canUseCode: boolean;
  onShowLeaderboard: () => void;
  onCopyCode: () => void;
  onShareCode: () => void;
  onGenerateNewCode: () => void;
  events?: LeaderboardEventWithUserStatus[];
  onShowEvents: () => void;
  userPoints?: number;
  userMultiplier?: number;
}

export const NewReferralScreen: React.FC<NewReferralScreenProps> = ({
  referralCode,
  referralCount,
  codeNeedsRefresh,
  // codeUsedCount,
  // codeMaxUses,
  canUseCode,
  onShowLeaderboard,
  onCopyCode,
  onShareCode,
  onGenerateNewCode,
  events = [],
  onShowEvents,
  userPoints = 0,
  userMultiplier = 1,
}) => {
  const handleCopyCode = async () => {
    onCopyCode();
    toast.success("Code copied to clipboard");
  };

  return (
    <div className="text-text-primary">
      {/* Content */}
      <div className="px-6 space-y-6 mt-[10vw]">
        {/* Top Referrers Card */}
        <div className="bg-tertiary border border-border rounded-2xl p-3 space-y-3">
          {/* Stats Section - Three Circular Progresses */}
          <div className="space-y-4">
            {/* Three Circular Progress Indicators */}
            <div className="flex items-center justify-between gap-2 sm:gap-4">
              {/* Referrals */}
              <div className="flex-1 flex flex-col items-center gap-2 min-w-0">
                <div className="text-text-secondary text-xs font-medium">
                  Referrals
                </div>
                <div className="w-full max-w-[100px] sm:max-w-[100px]">
                  <CircularProgress
                    progress={Math.max(
                      Math.min((referralCount / 100) * 100, 100),
                      1
                    )}
                    strokeWidth={6}
                    value={referralCount}
                  />
                </div>
              </div>

              {/* Points */}
              <div className="flex-1 flex flex-col items-center gap-2 min-w-0">
                <div className="text-text-secondary text-xs font-medium flex items-center gap-1">
                  Points
                </div>
                <div className="w-full max-w-[100px] sm:max-w-[100px]">
                  <CircularProgress
                    progress={Math.max(
                      Math.min((userPoints / 1000) * 100, 1000),
                      1
                    )}
                    strokeWidth={6}
                    value={userPoints}
                  />
                </div>
              </div>

              {/* Multiplier */}
              <div className="flex-1 flex flex-col items-center gap-2 min-w-0">
                <div className="text-text-secondary text-xs font-medium">
                  Multiplier
                </div>
                <div className="w-full max-w-[100px] sm:max-w-[100px]">
                  <CircularProgress
                    progress={100}
                    strokeWidth={6}
                    value={userMultiplier}
                  />
                </div>
              </div>
            </div>

            {/* Separator */}
            <div className="border-t border-border"></div>

            {/* Referral Count Text */}
            <div className="text-center text-text-primary text-sm">
              You've Referred{" "}
              <span className="text-accent font-semibold">{referralCount}</span>{" "}
              Friend{referralCount !== 1 ? "s" : ""}
            </div>

            {/* Show Leaderboard Button */}
            <button
              onClick={onShowLeaderboard}
              className="w-full py-2 px-4 bg-accent/5 hover:bg-accent/30 active:bg-accent/40 active:scale-[0.98] text-accent-light border border-accent/30 rounded-lg transition-all duration-150 flex items-center justify-center gap-2 active:border-accent/50"
            >
              <span>Show Leaderboard</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Referral Code Card */}
        <div
          className="rounded-2xl p-6 relative overflow-hidden"
          style={{
            backgroundImage: "url(/cardBg.png)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          {/* Content */}
          <div className="relative z-10">
            <h3 className="text-md text-text-primary mb-2 text-start">
              Referral Code
              {/* <span className='text-text-secondary text-xs'>Used ({codeUsedCount} of {codeMaxUses} times)</span> */}
            </h3>

            {canUseCode ? (
              <>
                {/* Code Display */}
                <div className="bg-primary border border-border rounded-lg px-2 mb-3 w-full">
                  <div className="flex items-center w-full gap-1">
                    <input
                      type="text"
                      value={codeNeedsRefresh ? "EXPIRED" : referralCode}
                      readOnly
                      id="referral-code"
                      name="referral-code"
                      className={`bg-transparent opacity-90 font-mono text-lg flex-1 outline-none cursor-default text-center truncate min-w-0 ${
                        codeNeedsRefresh ? "text-red-500" : "text-text-primary"
                      }`}
                      style={{ width: 0 }}
                    />
                    {!codeNeedsRefresh && (
                      <div className="border-l border-border flex-shrink-0 h-full py-2">
                        <button
                          onClick={handleCopyCode}
                          className="ml-1 px-2 py-1 text-accent rounded-lg transition-colors flex items-center gap-2 text-sm"
                          style={{ whiteSpace: "nowrap" }}
                        >
                          <Copy className="w-4 h-4" />
                          <span className="text-white opacity-90 text-md">
                            Copy
                          </span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Share or Generate New Code Button */}
                {codeNeedsRefresh ? (
                  <button
                    onClick={onGenerateNewCode}
                    className="w-full py-2 text-sm bg-accent border border-primary/50 text-text-primary font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    Get New Code
                    <Zap className="w-5 h-5 text-accent-light" />
                  </button>
                ) : (
                  <button
                    onClick={onShareCode}
                    className="w-full py-[10px] text-sm bg-accent border border-primary/50 text-text-primary font-medium rounded-lg transition-all duration-150 active:scale-[0.98] active:bg-accent/90 active:border-accent/70 flex items-center justify-center gap-2 animate-pulse-subtle"
                  >
                    Share Code
                    <Share className="w-5 h-5 text-accent-light" />
                  </button>
                )}
              </>
            ) : (
              /* Information message when canUseCode is false */
              <div className="bg-primary border border-border rounded-lg p-4 w-full space-y-3">
                <p className="text-text-secondary text-sm text-center">
                  Connect your first exchange to trigger to get your referral
                  code and start earning{" "}
                  <u className="text-accent-light">$TRIGGER</u>.
                </p>
                <Link
                  to="/settings"
                  className="w-full py-[10px] text-sm bg-accent border border-primary/50 text-text-primary font-medium rounded-lg transition-all duration-150 active:scale-[0.98] active:bg-accent/90 active:border-accent/70 flex items-center justify-center gap-2 animate-pulse-subtle"
                >
                  Connect Exchange
                  <Zap className="w-5 h-5 text-white" />
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Level Up Your Trading Section */}
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-text-primary">
              Level Up Your Trading
            </h3>
            <button
              onClick={onShowEvents}
              className="text-accent-light text-sm font-medium hover:text-accent transition-colors"
            >
              See All
            </button>
          </div>

          {/* Event Tiles */}
          <div className="space-y-3">
            {events?.slice(0, 3).map((event) => (
              <div
                key={event.id}
                className="bg-tertiary border border-border rounded-xl p-4 flex items-center justify-between"
              >
                <div className="flex-1">
                  <div className="text-text-primary font-medium">
                    {event.name}
                  </div>
                  <div className="text-sm text-text-secondary">
                    {event.description}
                  </div>
                </div>
                <div className="text-right ml-4 flex flex-col items-end gap-2 justify-between">
                  {event.eventType === "LIMITED" ? (
                    <div
                      className={`text-xs ${event.completed ? "text-accent bg-accent/5 border border-accent text-accent-light" : "text-red-200 bg-red-500/5 border border-red-500 text-red-500"}
                        rounded-full
                        px-2 py-1`}
                    >
                      {event.completed ? "Completed" : "Not completed"}
                    </div>
                  ) : (
                    <div className="text-xs text-text-secondary mt-1">
                      {event.completedCount || 0} times
                    </div>
                  )}
                  <div className="text-accent-light font-semibold">
                    {" "}
                    {event.increaseMultiplier
                      ? `+${event.increaseMultiplier / 100}x`
                      : `+${event.points}`}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          {/* How it works */}
          <HowItWorks className="py-3" />
        </div>
      </div>
    </div>
  );
};
